import { type ComponentType, type ReactNode } from 'react';
import InboxIcon from '@mui/icons-material/InboxOutlined';
import TrackerIcon from '@mui/icons-material/ExploreOutlined';
import WorkflowIcon from '@mui/icons-material/PlayArrow';
import {
	ActivityDashboardIcon,
	AdvancedMenuIcon,
	DashboardIconFill,
	DashboardIconLine,
	BsnIconLine,
	BsnIconFill,
	DeviceIcon,
	DeviceIconFill,
	DeviceIconLine,
	FormIcon,
	FormIconFill,
	FormIconLine,
	HomeMenuIcon,
	LicenseGroupIcon,
	LogIconFill,
	LogIconLine,
	LogsIcon,
	AppsIcon,
	ReportsIcon,
	ReportsExpandIcon,
	ReportsPageIcon,
	RolesIcon,
	RolesIconFill,
	RolesIconLine,
	ReportsIconLine,
	ReportsIconFill,
	TenantIconFill,
	TenantIconLine,
	UserIcon,
	UserIconFill,
	UserIconLine,
	WorkflowIconFill,
	WorkflowIconLine,
	JobQueueIcon,
	JobsIcon,
	DirectPrintMenuIcon,
	ManagedDirectPrintIcon,
	LicenseIconLine,
	LicenseIconFill,
	NamingIconFill,
	NamingIconLine,
	NamingIcon,
	PrintsMenuIcon,
	ManagedPrintsIcon,
	PrintsIcon,
	MFPIcon,
	DeleteIcon,
	DocManagementIcon,
} from 'app/components/Icons';
import { licenseGroupPlanDesignCheck } from 'app/utils/tenant-plan';
import { Inventory2Outlined } from '@mui/icons-material';

type NavigationConfig = {
	id: string;
	Icon: ComponentType;
	LineIcon: ComponentType;
	FillIcon: ComponentType;
	section: 'admin' | 'manager';
	children?: NavigationConfig[];
} & (
	| {
			title: ReactNode;
			translate?: never;
	  }
	| {
			title?: never;
			translate: string; // translates in `common.json` (see `FuseNavVerticalItem.js` and `FuseNavHorizontalItem.js`)
	  }
) &
	(
		| {
				type: 'item';
				url: string;
		  }
		| {
				type: 'collapse' | 'group';
				url?: string;
		  }
	);

const navigationConfigStarter: NavigationConfig[] = [
	{
		id: 'workflows',
		translate: 'Workflows',
		type: 'item',
		Icon: WorkflowIcon,
		LineIcon: WorkflowIconLine,
		FillIcon: WorkflowIconFill,
		url: '/admin/workflows',
		section: 'admin',
	},
	{
		id: 'dashboards',
		translate: 'Dashboards',
		type: 'collapse',
		Icon: ReportsExpandIcon,
		LineIcon: ReportsExpandIcon,
		FillIcon: ReportsExpandIcon,
		section: 'admin',
		children: [
			{
				id: 'activity',
				translate: 'Activity',
				type: 'item',
				Icon: ActivityDashboardIcon,
				LineIcon: ActivityDashboardIcon,
				FillIcon: ActivityDashboardIcon,
				url: '/admin/dashboard',
				section: 'admin',
			},
			{
				id: 'bsn',
				translate: 'bSN',
				type: 'item',
				Icon: ReportsIcon,
				LineIcon: BsnIconLine,
				FillIcon: BsnIconFill,
				url: '/admin/bsn',
				section: 'admin',
			},
			{
				id: 'mfp',
				translate: 'MFP',
				type: 'item',
				Icon: MFPIcon,
				LineIcon: MFPIcon,
				FillIcon: MFPIcon,
				url: '/admin/dashboards/mfp',
				section: 'admin',
			},
		],
	},
	{
		id: 'direct',
		translate: 'Direct Print',
		type: 'collapse',
		Icon: DirectPrintMenuIcon,
		LineIcon: DirectPrintMenuIcon,
		FillIcon: DirectPrintMenuIcon,
		section: 'admin',
		children: [
			{
				id: 'my-qs-direct',
				translate: 'My Qs',
				type: 'item',
				Icon: PrintsIcon,
				LineIcon: PrintsIcon,
				FillIcon: PrintsIcon,
				url: '/admin/my-qs-direct',
				section: 'admin',
			},
			{
				id: 'manage-qs-direct',
				translate: 'Management',
				type: 'item',
				Icon: ManagedDirectPrintIcon,
				LineIcon: ManagedDirectPrintIcon,
				FillIcon: ManagedDirectPrintIcon,
				url: '/admin/manage-qs-direct',
				section: 'admin',
			},
		],
	},
	{
		id: 'reports',
		translate: 'Reports',
		type: 'item',
		Icon: ReportsPageIcon,
		LineIcon: ReportsPageIcon,
		FillIcon: ReportsPageIcon,
		url: '/admin/reports',
		section: 'admin',
	},
	{
		id: 'files',
		translate: 'files:storage',
		type: 'collapse',
		Icon: DocManagementIcon,
		LineIcon: DocManagementIcon,
		FillIcon: DocManagementIcon,
		section: 'admin',
		children: [
			{
				id: 'archive',
				translate: 'files:archive page:title',
				type: 'item',
				Icon: Inventory2Outlined,
				LineIcon: Inventory2Outlined,
				FillIcon: Inventory2Outlined,
				url: '/admin/archive',
				section: 'admin',
			},
			{
				id: 'trash',
				translate: 'files:trash',
				type: 'item',
				Icon: DeleteIcon,
				LineIcon: DeleteIcon,
				FillIcon: DeleteIcon,
				url: '/admin/trash',
				section: 'admin',
			},
		],
	},
	{
		id: 'r2m',
		translate: 'Release2Me',
		type: 'collapse',
		Icon: PrintsMenuIcon,
		LineIcon: PrintsMenuIcon,
		FillIcon: PrintsMenuIcon,
		section: 'admin',
		children: [
			{
				id: 'my-qs',
				translate: 'My Qs',
				type: 'item',
				Icon: PrintsIcon,
				LineIcon: PrintsIcon,
				FillIcon: PrintsIcon,
				url: '/admin/my-qs',
				section: 'admin',
			},
			{
				id: 'manage-qs',
				translate: 'Manage Qs',
				type: 'item',
				Icon: ManagedPrintsIcon,
				LineIcon: ManagedPrintsIcon,
				FillIcon: ManagedPrintsIcon,
				url: '/admin/manage-qs',
				section: 'admin',
			},
		],
	},
	{
		id: 'devices',
		translate: 'Devices',
		type: 'item',
		Icon: DeviceIcon,
		LineIcon: DeviceIconLine,
		FillIcon: DeviceIconFill,
		url: '/admin/devices',
		section: 'admin',
	},
	{
		id: 'users',
		translate: 'Users',
		type: 'item',
		Icon: UserIcon,
		LineIcon: UserIconLine,
		FillIcon: UserIconFill,
		url: '/admin/users',
		section: 'admin',
	},
	{
		id: 'forms',
		translate: 'Forms',
		type: 'item',
		Icon: FormIcon,
		LineIcon: FormIconLine,
		FillIcon: FormIconFill,
		url: '/admin/forms',
		section: 'admin',
	},
	{
		id: 'logs',
		translate: 'Logs',
		type: 'item',
		Icon: LogsIcon,
		LineIcon: LogIconLine,
		FillIcon: LogIconFill,
		url: '/admin/logs',
		section: 'admin',
	},
	// {
	// 	id: 'input',
	// 	translate: 'Licenses',
	// 	type: 'item',
	// 	Icon: LogsIcon,
	// 	LineIcon: LicenseIconLine,
	// 	FillIcon: LicenseIconFill,
	// 	url: '/admin/input',
	// 	section: 'admin'
	// }
];

const navigationConfigEnterprise: NavigationConfig[] = [
	{
		id: 'home',
		translate: 'Home',
		type: 'collapse',
		Icon: HomeMenuIcon,
		LineIcon: HomeMenuIcon,
		FillIcon: HomeMenuIcon,
		url: '/admin/home',
		section: 'admin',
		children: [
			{
				id: 'inbox',
				translate: 'Inbox',
				type: 'item',
				Icon: InboxIcon,
				LineIcon: InboxIcon,
				FillIcon: InboxIcon,
				url: '/admin/home/inbox',
				section: 'admin',
			},
			{
				id: 'jobs',
				translate: 'Jobs',
				type: 'item',
				Icon: JobsIcon,
				LineIcon: JobsIcon,
				FillIcon: JobsIcon,
				url: '/admin/home/jobs',
				section: 'admin',
			},
			{
				id: 'job-queue',
				translate: 'Job Queues',
				type: 'item',
				Icon: JobQueueIcon,
				LineIcon: JobQueueIcon,
				FillIcon: JobQueueIcon,
				url: '/admin/home/job-queue',
				section: 'admin',
			},
			{
				id: 'tracker',
				translate: 'Tracker',
				type: 'item',
				Icon: TrackerIcon,
				LineIcon: TrackerIcon,
				FillIcon: TrackerIcon,
				url: '/admin/home/tracker',
				section: 'admin',
			},
		],
	},
	{
		id: 'dashboards',
		translate: 'Dashboards',
		type: 'collapse',
		Icon: ReportsExpandIcon,
		LineIcon: ReportsExpandIcon,
		FillIcon: ReportsExpandIcon,
		section: 'admin',
		children: [
			{
				id: 'activity',
				translate: 'Activity',
				type: 'item',
				Icon: ActivityDashboardIcon,
				LineIcon: ActivityDashboardIcon,
				FillIcon: ActivityDashboardIcon,
				url: '/admin/dashboard',
				section: 'admin',
			},
			{
				id: 'bsn',
				translate: 'bSN',
				type: 'item',
				Icon: ReportsIcon,
				LineIcon: BsnIconLine,
				FillIcon: BsnIconFill,
				url: '/admin/bsn',
				section: 'admin',
			},
			{
				id: 'mfp',
				translate: 'MFP',
				type: 'item',
				Icon: MFPIcon,
				LineIcon: MFPIcon,
				FillIcon: MFPIcon,
				url: '/admin/dashboards/mfp',
				section: 'admin',
			},
		],
	},
	{
		id: 'direct',
		translate: 'Direct Print',
		type: 'collapse',
		Icon: DirectPrintMenuIcon,
		LineIcon: DirectPrintMenuIcon,
		FillIcon: DirectPrintMenuIcon,
		section: 'admin',
		children: [
			{
				id: 'my-qs-direct',
				translate: 'My Qs',
				type: 'item',
				Icon: PrintsIcon,
				LineIcon: PrintsIcon,
				FillIcon: PrintsIcon,
				url: '/admin/my-qs-direct',
				section: 'admin',
			},
			{
				id: 'manage-qs-direct',
				translate: 'Management',
				type: 'item',
				Icon: ManagedDirectPrintIcon,
				LineIcon: ManagedDirectPrintIcon,
				FillIcon: ManagedDirectPrintIcon,
				url: '/admin/manage-qs-direct',
				section: 'admin',
			},
		],
	},
	{
		id: 'apps',
		translate: 'Apps',
		type: 'item',
		Icon: AppsIcon,
		LineIcon: AppsIcon,
		FillIcon: AppsIcon,
		url: '/admin/apps',
		section: 'admin',
	},
	{
		id: 'workflows',
		translate: 'Workflows',
		type: 'item',
		Icon: WorkflowIcon,
		LineIcon: WorkflowIconLine,
		FillIcon: WorkflowIconFill,
		url: '/admin/workflows',
		section: 'admin',
	},
	{
		id: 'reports',
		translate: 'Reports',
		type: 'item',
		Icon: ReportsPageIcon,
		LineIcon: ReportsPageIcon,
		FillIcon: ReportsPageIcon,
		url: '/admin/reports',
		section: 'admin',
	},
	{
		id: 'files',
		translate: 'files:storage',
		type: 'collapse',
		Icon: DocManagementIcon,
		LineIcon: DocManagementIcon,
		FillIcon: DocManagementIcon,
		section: 'admin',
		children: [
			{
				id: 'archive',
				translate: 'files:archive page:title',
				type: 'item',
				Icon: Inventory2Outlined,
				LineIcon: Inventory2Outlined,
				FillIcon: Inventory2Outlined,
				url: '/admin/archive',
				section: 'admin',
			},
			{
				id: 'trash',
				translate: 'files:trash',
				type: 'item',
				Icon: DeleteIcon,
				LineIcon: DeleteIcon,
				FillIcon: DeleteIcon,
				url: '/admin/trash',
				section: 'admin',
			},
		],
	},
	{
		id: 'r2m',
		translate: 'Release2Me',
		type: 'collapse',
		Icon: PrintsMenuIcon,
		LineIcon: PrintsMenuIcon,
		FillIcon: PrintsMenuIcon,
		section: 'admin',
		children: [
			{
				id: 'my-qs',
				translate: 'My Qs',
				type: 'item',
				Icon: PrintsIcon,
				LineIcon: PrintsIcon,
				FillIcon: PrintsIcon,
				url: '/admin/my-qs',
				section: 'admin',
			},
			{
				id: 'manage-qs',
				translate: 'Manage Qs',
				type: 'item',
				Icon: ManagedPrintsIcon,
				LineIcon: ManagedPrintsIcon,
				FillIcon: ManagedPrintsIcon,
				url: '/admin/manage-qs',
				section: 'admin',
			},
		],
	},
	{
		id: 'advanced',
		translate: 'Advanced',
		type: 'collapse',
		Icon: AdvancedMenuIcon,
		LineIcon: AdvancedMenuIcon,
		FillIcon: AdvancedMenuIcon,
		section: 'admin',
		children: [
			{
				id: 'devices',
				translate: 'Devices',
				type: 'item',
				Icon: DeviceIcon,
				LineIcon: DeviceIconLine,
				FillIcon: DeviceIconFill,
				url: '/admin/devices',
				section: 'admin',
			},
			{
				id: 'users',
				translate: 'Users',
				type: 'item',
				Icon: UserIcon,
				LineIcon: UserIconLine,
				FillIcon: UserIconFill,
				url: '/admin/users',
				section: 'admin',
			},
			{
				id: 'forms',
				translate: 'Forms',
				type: 'item',
				Icon: FormIcon,
				LineIcon: FormIconLine,
				FillIcon: FormIconFill,
				url: '/admin/forms',
				section: 'admin',
			},
			{
				id: 'naming',
				translate: 'Naming',
				type: 'item',
				Icon: NamingIcon,
				LineIcon: NamingIconLine,
				FillIcon: NamingIconFill,
				url: '/admin/naming',
				section: 'admin',
			},
			{
				id: 'logs',
				translate: 'Logs',
				type: 'item',
				Icon: LogsIcon,
				LineIcon: LogIconLine,
				FillIcon: LogIconFill,
				url: '/admin/logs',
				section: 'admin',
			},
			{
				id: 'roles',
				translate: 'Roles',
				type: 'item',
				Icon: RolesIcon,
				LineIcon: RolesIconLine,
				FillIcon: RolesIconFill,
				url: '/admin/roles',
				section: 'admin',
			},
			// {
			// 	id: 'input',
			// 	translate: 'Licenses',
			// 	type: 'item',
			// 	Icon: RolesIcon,
			// 	LineIcon: LicenseIconLine,
			// 	FillIcon: LicenseIconFill,
			// 	url: '/admin/input',
			// 	section: 'admin'
			// }
		],
	},
];

const managerNavigationConfig: NavigationConfig[] = [
	{
		id: 'managed-license-groups',
		translate: 'Tenants',
		type: 'item',
		Icon: LicenseGroupIcon,
		LineIcon: TenantIconLine,
		FillIcon: TenantIconFill,
		url: '/manager/tenants',
		section: 'manager',
	},
	{
		id: 'manager-dashboard',
		translate: 'Dashboard',
		type: 'item',
		Icon: ReportsIcon,
		LineIcon: DashboardIconLine,
		FillIcon: DashboardIconFill,
		url: '/manager/dashboard',
		section: 'manager',
	},
	{
		id: 'manager-reports',
		translate: 'Reports',
		type: 'item',
		Icon: ReportsIcon,
		LineIcon: ReportsIconLine,
		FillIcon: ReportsIconFill,
		url: '/manager/reports',
		section: 'manager',
	},
];

const adminNavigationConfig = licenseGroupPlanDesignCheck('stratus')
	? navigationConfigEnterprise
	: navigationConfigStarter;

const navigationConfig = [...adminNavigationConfig, ...managerNavigationConfig];

export default navigationConfig;
